/**
 * Catalog Offline Service
 * Manages offline caching for catalog listings using IndexedDB
 */

const DB_NAME = 'drug-app-db';
const DB_VERSION = 3; // Updated to match favorites version
const CATALOG_STORE = 'catalog-cache';

// TTL for cached data (24 hours)
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
// Max items to cache per filter combination
const MAX_ITEMS_PER_FILTER = 100;

interface CatalogCache {
  filterKey: string;
  items: unknown[];
  hasMore: boolean;
  cursor: { createdAt: string; listingId: string } | null;
  savedAt: number;
}

/**
 * Open IndexedDB connection with catalog store
 */
async function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => {
      reject(new Error('Failed to open IndexedDB for catalog'));
    };
    
    request.onsuccess = () => {
      resolve(request.result);
    };
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      // Create catalog store
      if (!db.objectStoreNames.contains(CATALOG_STORE)) {
        db.createObjectStore(CATALOG_STORE, { keyPath: 'filterKey' });
      }
      
      // Create chat stores if missing
      if (!db.objectStoreNames.contains('chat-rooms')) {
        db.createObjectStore('chat-rooms', { keyPath: 'userId' });
      }
      if (!db.objectStoreNames.contains('chat-messages')) {
        db.createObjectStore('chat-messages', { keyPath: 'roomId' });
      }
      
      // Create draft-photos store if missing
      if (!db.objectStoreNames.contains('draft-photos')) {
        db.createObjectStore('draft-photos', { keyPath: 'draftId' });
      }
      
      // Create favorites store if missing (v3)
      if (!db.objectStoreNames.contains('favorites-cache')) {
        db.createObjectStore('favorites-cache', { keyPath: 'id' });
      }
    };
  });
}

/**
 * Save catalog data to IndexedDB
 */
export async function saveCatalogToIDB(
  filterKey: string,
  items: unknown[],
  hasMore: boolean,
  cursor: { createdAt: string; listingId: string } | null
): Promise<void> {
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const tx = db.transaction(CATALOG_STORE, 'readwrite');
      const store = tx.objectStore(CATALOG_STORE);
      
      // Limit items to prevent exceeding storage quota
      const limitedItems = items.slice(0, MAX_ITEMS_PER_FILTER);
      
      const record: CatalogCache = {
        filterKey,
        items: limitedItems,
        hasMore,
        cursor,
        savedAt: Date.now(),
      };
      
      store.put(record);
      
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      
      tx.onerror = () => {
        db.close();
        reject(new Error('Failed to save catalog to IndexedDB'));
      };
    });
  } catch {
    // IndexedDB not available - fail silently
  }
}

/**
 * Load catalog data from IndexedDB
 */
export async function loadCatalogFromIDB(filterKey: string): Promise<{
  items: unknown[];
  hasMore: boolean;
  cursor: { createdAt: string; listingId: string } | null;
} | null> {
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const tx = db.transaction(CATALOG_STORE, 'readonly');
      const store = tx.objectStore(CATALOG_STORE);
      const request = store.get(filterKey);
      
      request.onsuccess = () => {
        db.close();
        const record = request.result as CatalogCache | undefined;
        
        if (!record) {
          resolve(null);
          return;
        }
        
        // Check TTL
        if (Date.now() - record.savedAt > CACHE_TTL_MS) {
          resolve(null);
          return;
        }
        
        resolve({
          items: record.items,
          hasMore: record.hasMore,
          cursor: record.cursor,
        });
      };
      
      request.onerror = () => {
        db.close();
        reject(new Error('Failed to load catalog from IndexedDB'));
      };
    });
  } catch {
    return null;
  }
}

/**
 * Clear catalog cache
 */
export async function clearCatalogCache(): Promise<void> {
  try {
    const db = await openDB();
    
    return new Promise((resolve) => {
      const tx = db.transaction(CATALOG_STORE, 'readwrite');
      const store = tx.objectStore(CATALOG_STORE);
      store.clear();
      
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      
      tx.onerror = () => {
        db.close();
        resolve();
      };
    });
  } catch {
    // Silent fail
  }
}

/**
 * Cleanup old cached data (older than TTL)
 */
export async function cleanupOldCatalogCache(): Promise<void> {
  try {
    const db = await openDB();
    const now = Date.now();
    
    return new Promise((resolve) => {
      const tx = db.transaction(CATALOG_STORE, 'readwrite');
      const store = tx.objectStore(CATALOG_STORE);
      const request = store.openCursor();
      
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          const record = cursor.value as CatalogCache;
          if (now - record.savedAt > CACHE_TTL_MS) {
            cursor.delete();
          }
          cursor.continue();
        }
      };
      
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      
      tx.onerror = () => {
        db.close();
        resolve();
      };
    });
  } catch {
    // Silent fail
  }
}

export default {
  saveCatalogToIDB,
  loadCatalogFromIDB,
  clearCatalogCache,
  cleanupOldCatalogCache,
};
